// 단계 깔때기 — 코호트 = 기간(KST) 안에 문의(고객 등록)한 고객, 단계 = 문의·연락(첫 응대·상담 이후 단계)·견적 발송(가견적 제외 초안 아닌 견적)·계약(체결·완료, 갈아 낀 옛 계약 제외)·준공(유지보수·완료 현장 또는 준공일), 뒷 단계에 닿았으면 앞 단계도 닿은 것으로 센다.

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { APP_TZ } from "../../lib/format";
import type { Contract } from "../contract/rows";
import { responseMinutes } from "../customer/first-contact";
import type { Customer, CustomerStage } from "../customer/types";
import { contractAmountByCustomer, countedContracts, inCohort } from "./source-funnel";
import { InsightRange, rangeMonths, rangeStart } from "./range";
import { FINISHED_PROJECT_STATUSES, type Project } from "../project/types";
import type { Quote } from "../quote/types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const FUNNEL_STEPS = ["INQUIRY", "CONTACTED", "QUOTE_SENT", "CONTRACTED", "FINISHED"] as const;
export type FunnelStepKey = (typeof FUNNEL_STEPS)[number];

export const FUNNEL_STEP_LABEL: Record<FunnelStepKey, string> = {
  INQUIRY: "문의",
  CONTACTED: "연락",
  QUOTE_SENT: "견적 발송",
  CONTRACTED: "계약",
  FINISHED: "준공",
};

// 앞 단계에서 넘어온 비율이 이 값 미만이면 경고 톤.
export const LOW_CONVERSION = 0.5;

export const LEAD_SPANS = ["INQUIRY_CONTACT", "CONTACT_QUOTE", "QUOTE_CONTRACT", "CONTRACT_START"] as const;
export type LeadSpanKey = (typeof LEAD_SPANS)[number];

export const LEAD_SPAN_LABEL: Record<LeadSpanKey, string> = {
  INQUIRY_CONTACT: "문의 → 연락",
  CONTACT_QUOTE: "연락 → 견적 발송",
  QUOTE_CONTRACT: "견적 발송 → 계약",
  CONTRACT_START: "계약 → 착공",
};

export const NO_ASSIGNEE_LABEL = "담당 없음";
export const GONE_ASSIGNEE_LABEL = "지난 담당자";

export type FunnelCustomer = Pick<Customer, "id" | "created_at" | "first_contacted_at" | "stage" | "assignee_id" | "meta">;
export type FunnelQuote = Pick<Quote, "customer_id" | "kind" | "status" | "sent_at">;
export type FunnelContract = Pick<Contract, "id" | "customer_id" | "status" | "total_amount" | "replaces_contract_id" | "signed_at">;
export type FunnelProject = Pick<Project, "customer_id" | "status" | "started_at" | "completion_date">;

export interface FunnelStep {
  key: FunnelStepKey;
  label: string;
  count: number;
  // 첫 단계 대비 — 첫 단계가 0 이면 null.
  share: number | null;
  // 앞 단계에서 넘어온 비율 — 첫 단계이거나 앞 단계가 0 이면 null.
  conversion: number | null;
  low: boolean;
}

export interface LeadSpan {
  key: LeadSpanKey;
  label: string;
  // 평균 일수(소수 첫째 자리) — 두 끝이 다 있는 고객이 없으면 null.
  avg_days: number | null;
  samples: number;
  bottleneck: boolean;
}

export interface MonthBucket {
  key: string;
  label: string;
  inquiries: number;
  won: number;
}

export interface DesignerRate {
  assignee_id: string | null;
  name: string;
  won: number;
  quoted: number;
  rate: number;
}

export interface StageInsight {
  inquiries: number;
  funnel: FunnelStep[];
  months: MonthBucket[];
  lead: LeadSpan[];
  designers: DesignerRate[];
}

export interface StageInsightInput {
  customers: readonly FunnelCustomer[];
  quotes: readonly FunnelQuote[];
  contracts: readonly FunnelContract[];
  projects: readonly FunnelProject[];
  memberName: ReadonlyMap<string, string>;
  range: InsightRange;
  now: Date | string;
}

export const FUNNEL_CONTRACT_NOTE = "준공까지 간 고객은 계약에도 셉니다 — 계약이 해지·무효돼도 세므로 아래 표와 다를 수 있어요.";

// 깔때기 「계약」 수가 아래 유입 경로 표의 계약 수(체결 계약만)와 다를 때만 설명을 붙인다.
export const funnelContractDiffers = (funnel: readonly FunnelStep[], tableContracted: number): boolean => {
  const step = funnel.find((s) => s.key === "CONTRACTED");
  return step !== undefined && step.count !== tableContracted;
};

const DAY_MS = 86_400_000;
const CONTACTED_STAGES = new Set<CustomerStage>(["CONTACTED", "QUOTED", "WON"]);
const FINISHED = new Set<string>(FINISHED_PROJECT_STATUSES);

const rate = (n: number, d: number): number | null => (d > 0 ? n / d : null);

// 가장 이른 시각 — 없으면 null.
const earlier = (a: string | null | undefined, b: string | null | undefined): string | null => {
  if (!a) return b ?? null;
  if (!b) return a;
  return dayjs(b).isBefore(dayjs(a)) ? b : a;
};

// 두 시각 사이 일수 — 한쪽이 없거나 거꾸로면(자료 오류) null.
const daysBetween = (from: string | null, to: string | null): number | null => {
  if (!from || !to) return null;
  const ms = dayjs(to).diff(dayjs(from));
  return Number.isFinite(ms) && ms >= 0 ? ms / DAY_MS : null;
};

const round1 = (n: number): number => Math.round(n * 10) / 10;

interface Journey {
  reached: Record<FunnelStepKey, boolean>;
  firstSentAt: string | null;
  firstSignedAt: string | null;
  firstStartedAt: string | null;
}

export const buildStageInsight = (input: StageInsightInput): StageInsight => {
  const since = rangeStart(input.range, input.now);
  const cohort = input.customers.filter((c) => inCohort(c.created_at, since));
  const ids = new Set(cohort.map((c) => c.id));

  const quoted = new Set<string>();
  const firstSent = new Map<string, string | null>();
  for (const q of input.quotes) {
    if (!ids.has(q.customer_id) || q.kind === "ROUGH") continue;
    if (q.status !== "DRAFT") quoted.add(q.customer_id);
    if (q.sent_at) firstSent.set(q.customer_id, earlier(firstSent.get(q.customer_id), q.sent_at));
  }

  const contracted = contractAmountByCustomer(input.contracts);
  const firstSigned = new Map<string, string | null>();
  // 해지·무효·갈아 낀 옛 계약의 체결일은 걸린 날에 쓰지 않는다 — 깔때기 계약 판정과 같은 기준.
  for (const c of countedContracts(input.contracts)) {
    if (ids.has(c.customer_id) && c.signed_at) firstSigned.set(c.customer_id, earlier(firstSigned.get(c.customer_id), c.signed_at));
  }

  const finished = new Set<string>();
  const firstStarted = new Map<string, string | null>();
  for (const p of input.projects) {
    if (!ids.has(p.customer_id)) continue;
    if (FINISHED.has(p.status) || p.completion_date) finished.add(p.customer_id);
    if (p.started_at) firstStarted.set(p.customer_id, earlier(firstStarted.get(p.customer_id), p.started_at));
  }

  const journeys = new Map<string, Journey>();
  for (const c of cohort) {
    const fin = finished.has(c.id);
    const con = fin || contracted.has(c.id);
    const sent = con || quoted.has(c.id);
    const contact = sent || !!c.first_contacted_at || CONTACTED_STAGES.has(c.stage);
    journeys.set(c.id, {
      reached: { INQUIRY: true, CONTACTED: contact, QUOTE_SENT: sent, CONTRACTED: con, FINISHED: fin },
      firstSentAt: firstSent.get(c.id) ?? null,
      firstSignedAt: firstSigned.get(c.id) ?? null,
      firstStartedAt: firstStarted.get(c.id) ?? null,
    });
  }

  const counts = FUNNEL_STEPS.map((key) => cohort.filter((c) => journeys.get(c.id)?.reached[key]).length);
  const funnel = FUNNEL_STEPS.map((key, i): FunnelStep => {
    const conversion = i === 0 ? null : rate(counts[i], counts[i - 1]);
    return {
      key,
      label: FUNNEL_STEP_LABEL[key],
      count: counts[i],
      share: rate(counts[i], counts[0]),
      conversion,
      low: conversion !== null && conversion < LOW_CONVERSION,
    };
  });

  const spanDays: Record<LeadSpanKey, number[]> = { INQUIRY_CONTACT: [], CONTACT_QUOTE: [], QUOTE_CONTRACT: [], CONTRACT_START: [] };
  for (const c of cohort) {
    const j = journeys.get(c.id)!;
    const response = responseMinutes(c);
    if (response !== null && response >= 0) spanDays.INQUIRY_CONTACT.push(response / 1440);
    const pairs: [LeadSpanKey, number | null][] = [
      ["CONTACT_QUOTE", daysBetween(c.first_contacted_at ?? c.created_at, j.firstSentAt)],
      ["QUOTE_CONTRACT", j.reached.CONTRACTED ? daysBetween(j.firstSentAt, j.firstSignedAt) : null],
      ["CONTRACT_START", j.reached.CONTRACTED ? daysBetween(j.firstSignedAt, j.firstStartedAt) : null],
    ];
    for (const [key, days] of pairs) if (days !== null) spanDays[key].push(days);
  }
  const avgs = LEAD_SPANS.map((key) => {
    const list = spanDays[key];
    return list.length > 0 ? round1(list.reduce((a, b) => a + b, 0) / list.length) : null;
  });
  const longest = avgs.reduce<number | null>((best, v) => (v !== null && v > 0 && (best === null || v > best) ? v : best), null);
  const bottleneckAt = longest === null ? -1 : avgs.indexOf(longest);
  const lead = LEAD_SPANS.map((key, i): LeadSpan => ({
    key,
    label: LEAD_SPAN_LABEL[key],
    avg_days: avgs[i],
    samples: spanDays[key].length,
    bottleneck: i === bottleneckAt,
  }));

  const months = rangeMonths(input.range, input.now).map((key): MonthBucket => ({ key, label: `${key.slice(5)}월`, inquiries: 0, won: 0 }));
  const monthAt = new Map(months.map((m) => [m.key, m]));
  for (const c of cohort) {
    const bucket = monthAt.get(dayjs(c.created_at).tz(APP_TZ).format("YYYY-MM"));
    if (!bucket) continue;
    bucket.inquiries += 1;
    if (journeys.get(c.id)?.reached.CONTRACTED) bucket.won += 1;
  }

  const byAssignee = new Map<string | null, { won: number; quoted: number }>();
  for (const c of cohort) {
    const j = journeys.get(c.id)!;
    if (!j.reached.QUOTE_SENT) continue;
    const tally = byAssignee.get(c.assignee_id) ?? { won: 0, quoted: 0 };
    tally.quoted += 1;
    if (j.reached.CONTRACTED) tally.won += 1;
    byAssignee.set(c.assignee_id, tally);
  }
  const designers = Array.from(byAssignee.entries())
    .map(([id, t]): DesignerRate => ({
      assignee_id: id,
      name: id === null ? NO_ASSIGNEE_LABEL : input.memberName.get(id) ?? GONE_ASSIGNEE_LABEL,
      won: t.won,
      quoted: t.quoted,
      rate: t.won / t.quoted,
    }))
    .sort((a, b) => b.rate - a.rate || b.quoted - a.quoted || a.name.localeCompare(b.name, "ko"));

  return { inquiries: cohort.length, funnel, months, lead, designers };
};
